// ThemeContext.jsx
import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";

import "../views/LandingPage/FlexOfficeDomain/flexOfficeTheme.css";
import { getEducationThemeCssVariables, useActiveEducationTheme } from "../template/EducationThemeBridge.jsx";
import "../template/EducationExamTemplate.css";
import { getFlexOfficeThemeCssVariables, useActiveFlexOfficeTheme } from "../template/FlexOfficeThemeBridge.jsx";

const ThemeContext = createContext();
const FLEX_OFFICE_THEME_KEY = "flexOfficeThemeMode";

const getStoredFlexOfficeTheme = () => {
  if (typeof window === "undefined") {
    return "light";
  }

  return localStorage.getItem(FLEX_OFFICE_THEME_KEY) === "dark" ? "dark" : "light";
};

const getStoredDomainType = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return localStorage.getItem("domainType") || "";
};

export const useTheme = () => {
  return useContext(ThemeContext);
};

export const ThemeProvider = ({ children, siteContext = null }) => {
  const educationTheme = useActiveEducationTheme(siteContext);
  const flexOfficeTheme = useActiveFlexOfficeTheme();
  const [themeMode, setThemeMode] = useState(getStoredFlexOfficeTheme);
  const [activeDomainType, setActiveDomainType] = useState(getStoredDomainType);
  const [isFlexOfficeScopeActive, setFlexOfficeScopeActive] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return localStorage.getItem("domainType") === "FLEX_OFFICE"
      || window.location.pathname.toLowerCase().includes("flexoffice")
      || window.location.pathname.toLowerCase().includes("cowork");
  });

  const darkMode = themeMode === "dark";
  const isEducationScopeActive = String(activeDomainType || "").toUpperCase() === "EDUCATION";
  const isFlexOfficeThemeActive = isFlexOfficeScopeActive && !isEducationScopeActive;
  const activeMode = isFlexOfficeScopeActive && darkMode ? "dark" : "light";

  const toggleDarkMode = () => {
    setThemeMode((prevMode) => {
      const nextMode = prevMode === "dark" ? "light" : "dark";
      localStorage.setItem(FLEX_OFFICE_THEME_KEY, nextMode);
      return nextMode;
    });
  };

  const setDarkMode = (enabled) => {
    const nextMode = enabled ? "dark" : "light";
    localStorage.setItem(FLEX_OFFICE_THEME_KEY, nextMode);
    setThemeMode(nextMode);
  };

  useEffect(() => {
    const refreshDomain = () => {
      const nextDomain = getStoredDomainType();
      setActiveDomainType(nextDomain);
      setFlexOfficeScopeActive(
        nextDomain === "FLEX_OFFICE"
        || window.location.pathname.toLowerCase().includes("flexoffice")
        || window.location.pathname.toLowerCase().includes("cowork")
      );
    };

    window.addEventListener("storage", refreshDomain);
    window.addEventListener("avps-template-updated", refreshDomain);
    window.addEventListener("focus", refreshDomain);
    return () => {
      window.removeEventListener("storage", refreshDomain);
      window.removeEventListener("avps-template-updated", refreshDomain);
      window.removeEventListener("focus", refreshDomain);
    };
  }, []);

  const theme = useMemo(() => ({
    mode: activeMode,
    primary: isEducationScopeActive ? educationTheme.primary : isFlexOfficeThemeActive ? flexOfficeTheme.primary : "#2563eb",
    primaryDark: isEducationScopeActive ? educationTheme.primaryDark : isFlexOfficeThemeActive ? flexOfficeTheme.primaryDark : activeMode === "dark" ? "#1d4ed8" : "#1e40af",
    secondary: isEducationScopeActive ? educationTheme.secondary : isFlexOfficeThemeActive ? flexOfficeTheme.accent : "#14b8a6",
    secondaryDark: isEducationScopeActive ? educationTheme.secondaryDark : isFlexOfficeThemeActive ? flexOfficeTheme.primaryDark : "#0f766e",
    background: isEducationScopeActive ? educationTheme.background : isFlexOfficeThemeActive ? (activeMode === "dark" ? "#0f172a" : flexOfficeTheme.background) : activeMode === "dark" ? "#0f172a" : "#f8fafc",
    surface: isEducationScopeActive ? educationTheme.surface : isFlexOfficeThemeActive ? (activeMode === "dark" ? "#111827" : flexOfficeTheme.surface) : activeMode === "dark" ? "#111827" : "#ffffff",
    color: isEducationScopeActive ? educationTheme.color : isFlexOfficeThemeActive ? (activeMode === "dark" ? "#f8fafc" : flexOfficeTheme.color) : activeMode === "dark" ? "#f8fafc" : "#0f172a",
    mutedColor: isEducationScopeActive ? educationTheme.mutedColor : isFlexOfficeThemeActive ? (activeMode === "dark" ? "#cbd5e1" : flexOfficeTheme.mutedColor) : activeMode === "dark" ? "#cbd5e1" : "#475569",
    borderColor: isEducationScopeActive ? educationTheme.borderColor : isFlexOfficeThemeActive ? (activeMode === "dark" ? "rgba(148, 163, 184, 0.24)" : flexOfficeTheme.borderColor) : activeMode === "dark" ? "rgba(148, 163, 184, 0.24)" : "rgba(15, 23, 42, 0.12)",
    buttonRadius: isEducationScopeActive ? educationTheme.buttonRadius : isFlexOfficeThemeActive ? flexOfficeTheme.buttonRadius : "12px",
    fontFamily: isEducationScopeActive ? educationTheme.fontFamily : isFlexOfficeThemeActive ? flexOfficeTheme.fontFamily : "'Inter', 'Segoe UI', Verdana, Geneva, Tahoma, sans-serif",
    headingFontFamily: isEducationScopeActive ? educationTheme.headingFontFamily : isFlexOfficeThemeActive ? flexOfficeTheme.headingFontFamily : undefined,
  }), [activeMode, educationTheme, flexOfficeTheme, isEducationScopeActive, isFlexOfficeThemeActive]);

  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    document.body.dataset.flexOfficeTheme = activeMode;
    document.documentElement.dataset.flexOfficeTheme = activeMode;
    document.documentElement.style.colorScheme = activeMode;
  }, [activeMode]);

  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const root = document.documentElement;
    const body = document.body;
    const variables = getEducationThemeCssVariables(educationTheme);

    if (isEducationScopeActive) {
      root.dataset.educationTheme = "active";
      body.dataset.educationTheme = "active";
      if (educationTheme.isResolved === false) {
        delete root.dataset.educationTemplateStyle;
        delete body.dataset.educationTemplateStyle;
      } else {
        root.dataset.educationTemplateStyle = educationTheme.templateStyle || "default";
        body.dataset.educationTemplateStyle = educationTheme.templateStyle || "default";
      }
      Object.entries(variables).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    } else {
      delete root.dataset.educationTheme;
      delete body.dataset.educationTheme;
      delete root.dataset.educationTemplateStyle;
      delete body.dataset.educationTemplateStyle;
      Object.keys(variables).forEach((key) => {
        root.style.removeProperty(key);
      });
    }
  }, [educationTheme, isEducationScopeActive]);

  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const root = document.documentElement;
    const body = document.body;
    const variables = getFlexOfficeThemeCssVariables(flexOfficeTheme, activeMode);

    if (isFlexOfficeThemeActive) {
      root.dataset.flexOfficeTemplateTheme = "active";
      body.dataset.flexOfficeTemplateTheme = "active";
      Object.entries(variables).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    } else {
      delete root.dataset.flexOfficeTemplateTheme;
      delete body.dataset.flexOfficeTemplateTheme;
      Object.keys(variables).forEach((key) => {
        root.style.removeProperty(key);
      });
    }
  }, [activeMode, flexOfficeTheme, isFlexOfficeThemeActive]);

  return (
    <ThemeContext.Provider
      value={{
        darkMode,
        themeMode,
        activeMode,
        theme,
        isFlexOfficeScopeActive,
        setDarkMode,
        setFlexOfficeScopeActive,
        toggleDarkMode,
      }}
    >
      <div
        className={`theme-root ${activeMode === "dark" ? "dark" : "light"} ${isEducationScopeActive ? "education-domain-theme" : ""} ${isFlexOfficeThemeActive ? "flex-office-domain-theme" : ""}`}
        data-theme-mode={activeMode}
        data-domain-theme={isEducationScopeActive ? "education" : isFlexOfficeThemeActive ? "flex-office" : undefined}
      >
        {isEducationScopeActive && (
          <style>{`
              .education-domain-theme {
                font-family: var(--education-font);
                color: var(--education-text);
                background: var(--education-background);
              }
              .education-domain-theme h1,
              .education-domain-theme h2,
              .education-domain-theme h3,
              .education-domain-theme h4,
              .education-domain-theme h5,
              .education-domain-theme h6,
              .education-domain-theme .primary-heading,
              .education-domain-theme .primary-heading2 {
                font-family: var(--education-heading-font) !important;
              }
              .education-domain-theme .primary-heading2,
              .education-domain-theme .primary-text2 {
                color: var(--education-primary) !important;
              }
              .education-domain-theme .secondary-button,
              .education-domain-theme .primary-button,
              .education-domain-theme .education-primary-button,
              .education-domain-theme .ui-button-primary,
              .education-domain-theme [data-ui-button="primary"] {
                background-color: var(--education-primary) !important;
                border-color: var(--education-primary) !important;
                color: #ffffff !important;
              }
              .education-domain-theme .secondary-button:hover,
              .education-domain-theme .primary-button:hover,
              .education-domain-theme .education-primary-button:hover,
              .education-domain-theme .ui-button-primary:hover,
              .education-domain-theme [data-ui-button="primary"]:hover {
                background-color: var(--education-primary-dark) !important;
              }
              .education-domain-theme .education-outline-secondary-button,
              .education-domain-theme .ui-button-outline,
              .education-domain-theme .ui-button-ghost,
              .education-domain-theme [data-ui-button="outline"],
              .education-domain-theme [data-ui-button="ghost"] {
                border-color: var(--education-primary) !important;
                color: var(--education-primary) !important;
              }
              .education-domain-theme .ui-tabs-indicator,
              .education-domain-theme .ui-linear-progress-bar,
              .education-domain-theme [data-ui-progress-bar] {
                background-color: var(--education-primary) !important;
              }
              .education-domain-theme .ui-chip-primary,
              .education-domain-theme .ui-avatar-primary,
              .education-domain-theme [data-ui-chip="primary"],
              .education-domain-theme [data-ui-avatar="primary"] {
                background-color: var(--education-primary-soft);
                color: var(--education-primary);
              }
              .education-domain-theme .is-selected,
              .education-domain-theme [aria-selected="true"],
              .education-domain-theme [data-selected="true"] {
                color: var(--education-primary);
              }
              .education-domain-theme [class~="text-blue-900"],
              .education-domain-theme [class~="text-blue-800"],
              .education-domain-theme [class~="text-blue-700"],
              .education-domain-theme [class~="text-blue-600"],
              .education-domain-theme [class~="text-blue-500"],
              .education-domain-theme [class~="text-indigo-700"],
              .education-domain-theme [class~="text-indigo-600"],
              .education-domain-theme [class~="text-indigo-500"] {
                color: var(--education-primary) !important;
              }
              .education-domain-theme [class~="text-blue-300"],
              .education-domain-theme [class~="text-blue-200"],
              .education-domain-theme [class~="text-blue-100"],
              .education-domain-theme [class~="text-indigo-300"],
              .education-domain-theme [class~="text-indigo-200"],
              .education-domain-theme [class~="text-indigo-100"] {
                color: var(--education-secondary) !important;
              }
              .education-domain-theme [class~="bg-blue-700"],
              .education-domain-theme [class~="bg-blue-600"],
              .education-domain-theme [class~="bg-blue-500"],
              .education-domain-theme [class~="bg-indigo-700"],
              .education-domain-theme [class~="bg-indigo-600"],
              .education-domain-theme [class~="bg-indigo-500"] {
                background-color: var(--education-primary) !important;
              }
              .education-domain-theme [class~="bg-blue-50"],
              .education-domain-theme [class~="bg-blue-100"],
              .education-domain-theme [class*="bg-blue-50/"],
              .education-domain-theme [class*="bg-blue-100/"],
              .education-domain-theme [class~="bg-indigo-50"],
              .education-domain-theme [class~="bg-indigo-100"],
              .education-domain-theme [class*="bg-indigo-50/"],
              .education-domain-theme [class*="bg-indigo-100/"] {
                background-color: var(--education-primary-soft) !important;
              }
              .education-domain-theme [class~="border-blue-100"],
              .education-domain-theme [class~="border-blue-200"],
              .education-domain-theme [class~="border-blue-300"],
              .education-domain-theme [class~="border-indigo-100"],
              .education-domain-theme [class~="border-indigo-200"],
              .education-domain-theme [class~="border-indigo-300"] {
                border-color: var(--education-primary-muted) !important;
              }
              .education-domain-theme [class~="border-blue-500"],
              .education-domain-theme [class~="border-blue-600"],
              .education-domain-theme [class~="border-indigo-500"],
              .education-domain-theme [class~="border-indigo-600"] {
                border-color: var(--education-primary) !important;
              }
              .education-domain-theme [class~="from-blue-700"],
              .education-domain-theme [class~="from-blue-600"],
              .education-domain-theme [class~="from-blue-500"],
              .education-domain-theme [class~="from-indigo-700"],
              .education-domain-theme [class~="from-indigo-600"],
              .education-domain-theme [class~="from-indigo-500"] {
                --tw-gradient-from: var(--education-primary) var(--tw-gradient-from-position) !important;
                --tw-gradient-to: rgba(255, 255, 255, 0) var(--tw-gradient-to-position) !important;
                --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important;
              }
              .education-domain-theme [class~="via-blue-600"],
              .education-domain-theme [class~="via-indigo-600"] {
                --tw-gradient-via: var(--education-secondary) var(--tw-gradient-via-position) !important;
              }
              .education-domain-theme [class~="to-blue-800"],
              .education-domain-theme [class~="to-blue-700"],
              .education-domain-theme [class~="to-blue-600"],
              .education-domain-theme [class~="to-indigo-800"],
              .education-domain-theme [class~="to-indigo-700"],
              .education-domain-theme [class~="to-indigo-600"] {
                --tw-gradient-to: var(--education-secondary) var(--tw-gradient-to-position) !important;
              }
              .education-domain-theme [class*="hover:text-blue"]:hover,
              .education-domain-theme [class*="hover:text-indigo"]:hover,
              .education-domain-theme .group:hover [class*="group-hover:text-blue"],
              .education-domain-theme .group:hover [class*="group-hover:text-indigo"] {
                color: var(--education-primary) !important;
              }
              .education-domain-theme [class*="hover:bg-blue-50"]:hover,
              .education-domain-theme [class*="hover:bg-blue-100"]:hover,
              .education-domain-theme [class*="hover:bg-indigo-50"]:hover,
              .education-domain-theme [class*="hover:bg-indigo-100"]:hover,
              .education-domain-theme .group:hover [class*="group-hover:bg-blue"],
              .education-domain-theme .group:hover [class*="group-hover:bg-indigo"] {
                background-color: var(--education-primary-soft) !important;
              }
              .education-domain-theme [class*="hover:bg-blue-500"]:hover,
              .education-domain-theme [class*="hover:bg-blue-600"]:hover,
              .education-domain-theme [class*="hover:bg-blue-700"]:hover,
              .education-domain-theme [class*="hover:bg-indigo-500"]:hover,
              .education-domain-theme [class*="hover:bg-indigo-600"]:hover,
              .education-domain-theme [class*="hover:bg-indigo-700"]:hover {
                background-color: var(--education-primary-dark) !important;
              }
              .education-domain-theme [class*="hover:border-blue"]:hover,
              .education-domain-theme [class*="hover:border-indigo"]:hover,
              .education-domain-theme .group:hover [class*="group-hover:border-blue"],
              .education-domain-theme .group:hover [class*="group-hover:border-indigo"] {
                border-color: var(--education-primary) !important;
              }
              .education-domain-theme [class*="focus:ring-blue"]:focus,
              .education-domain-theme [class*="focus:ring-blue"]:focus-visible,
              .education-domain-theme [class*="focus:ring-indigo"]:focus,
              .education-domain-theme [class*="focus:ring-indigo"]:focus-visible,
              .education-domain-theme [class*="focus:border-blue"]:focus,
              .education-domain-theme [class*="focus:border-indigo"]:focus {
                --tw-ring-color: var(--education-primary-muted) !important;
                border-color: var(--education-primary) !important;
              }
              .education-domain-theme [class*="shadow-blue"],
              .education-domain-theme [class*="shadow-indigo"] {
                box-shadow: 0 16px 40px var(--education-primary-shadow, rgba(116, 110, 172, 0.3)) !important;
              }
              .education-domain-theme .bg-gradient-to-r.from-blue-600.to-blue-700,
              .education-domain-theme .bg-gradient-to-r.from-blue-600.to-indigo-600,
              .education-domain-theme .bg-gradient-to-r.from-blue-600.to-indigo-700,
              .education-domain-theme .bg-gradient-to-r.from-blue-500.to-blue-600,
              .education-domain-theme .bg-gradient-to-r.from-blue-500.to-indigo-600,
              .education-domain-theme .bg-gradient-to-br.from-blue-600.to-blue-700,
              .education-domain-theme .bg-gradient-to-br.from-blue-600.to-indigo-600,
              .education-domain-theme .bg-gradient-to-br.from-blue-600.to-indigo-700 {
                background-image: var(--education-gradient) !important;
              }
          `}</style>
        )}
        {children}
      </div>
    </ThemeContext.Provider>
  );
};
